"use client";

/**
 * Forgot password form: submit email to request a reset link.
 * Shows generic success message to avoid email enumeration.
 */
import { useState, type JSX } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

export function ForgotPasswordForm(): JSX.Element {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setSubmitted(false);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (res.ok) setSubmitted(true);
      else setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <>
        <p className="mb-4 rounded bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/30 dark:text-green-200">
          If an account exists for that email, we&apos;ve sent a link to reset
          your password. Check your inbox and spam folder.
        </p>
        <p className="text-center text-sm text-muted-foreground">
          <Link
            href="/forgot-password"
            className="text-foreground font-medium underline"
          >
            Try another email
          </Link>
          {" · "}
          <Link href="/login" className="text-foreground font-medium underline">
            Back to sign in
          </Link>
        </p>
      </>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field>
          <FieldLabel htmlFor="forgot-email">Email</FieldLabel>
          <Input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
          />
        </Field>
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? "Sending…" : "Send reset link"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        <Link href="/login" className="text-foreground font-medium underline">
          Back to sign in
        </Link>
      </p>
    </>
  );
}
